import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

/* ============================================================
   CSS
   ============================================================ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=JetBrains+Mono:wght@500;700&family=Syne:wght@700;800;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .fs-root {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(200,255,0,0.045), transparent 26%),
      radial-gradient(circle at top right, rgba(255,255,255,0.025), transparent 20%),
      linear-gradient(180deg, #0a0f14 0%, #080c10 100%);
    color: #e2eaf4;
    font-family: 'DM Mono', monospace;
  }

  .fs-wrap { padding: 20px 16px 80px; }
  @media (min-width: 640px)  { .fs-wrap { padding: 28px 32px 80px; } }
  @media (min-width: 1024px) { .fs-wrap { padding: 36px 64px 80px; } }
  @media (min-width: 1400px) { .fs-wrap { padding: 40px 120px 80px; } }

  .fs-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 28px;
  }

  .fs-title-group {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  .fs-badge {
    font-family: 'Syne', sans-serif;
    font-weight: 900;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    background: #c8ff00;
    color: #080c10;
    padding: 4px 10px;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(200,255,0,0.2);
  }

  .fs-title {
    font-family: 'Syne', sans-serif;
    font-weight: 900;
    font-size: clamp(18px, 2.5vw, 26px);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #ffffff;
  }

  .fs-count-chip {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 999px;
    padding: 6px 12px;
    backdrop-filter: blur(6px);
  }

  .fs-card {
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 18px;
    overflow: hidden;
    box-shadow:
      0 12px 32px rgba(0,0,0,0.22),
      inset 0 1px 0 rgba(255,255,255,0.02);
    backdrop-filter: blur(10px);
  }

  .fs-card-header {
    padding: 14px 20px;
    background: linear-gradient(90deg, rgba(200,255,0,0.08), rgba(200,255,0,0.025));
    border-bottom: 1px solid rgba(200,255,0,0.1);
    font-family: 'Syne', sans-serif;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #d7ff57;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .fs-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    background: rgba(255,255,255,0.015);
  }

  .fs-toolbar-group {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .fs-label {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #64748b;
    white-space: nowrap;
  }

  .fs-select,
  .fs-input {
    height: 36px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 10px;
    color: #e2eaf4;
    padding: 0 12px;
    font-size: 12px;
    font-family: inherit;
    outline: none;
    transition: all 0.2s ease;
  }

  .fs-input-search { width: 260px; }

  .fs-select:focus,
  .fs-input:focus {
    border-color: rgba(200,255,0,0.55);
    box-shadow: 0 0 0 3px rgba(200,255,0,0.08);
    background: rgba(200,255,0,0.03);
  }

  .fs-select option { background: #10161d; color: #e2eaf4; }

  .fs-btn {
    height: 36px;
    padding: 0 14px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
    letter-spacing: 0.04em;
    border: none;
  }

  .fs-btn-accent {
    background: #c8ff00;
    color: #080c10;
    box-shadow: 0 4px 16px rgba(200,255,0,0.16);
  }
  .fs-btn-accent:hover:not(:disabled) {
    background: #d7ff57;
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(200,255,0,0.24);
  }

  .fs-btn-ghost {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    color: #94a3b8;
  }
  .fs-btn-ghost:hover:not(:disabled) {
    background: rgba(255,255,255,0.07);
    color: #e2eaf4;
    transform: translateY(-1px);
  }

  .fs-btn-excel {
    background: rgba(34,197,94,0.12);
    border: 1px solid rgba(34,197,94,0.28);
    color: #4ade80;
  }
  .fs-btn-excel:hover:not(:disabled) {
    background: rgba(34,197,94,0.2);
    transform: translateY(-1px);
  }

  .fs-btn:disabled, .fs-page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .fs-sep {
    width: 1px;
    height: 22px;
    background: linear-gradient(180deg, rgba(255,255,255,0), rgba(255,255,255,0.08), rgba(255,255,255,0));
    margin: 0 4px;
  }

  .fs-loading-banner {
    padding: 11px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    font-size: 11px;
    color: #94a3b8;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: rgba(255,255,255,0.015);
  }

  .fs-table-wrap { overflow-x: auto; }
  .fs-table-wrap::-webkit-scrollbar { height: 10px; }
  .fs-table-wrap::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); border-radius: 999px; }
  .fs-table-wrap::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 999px; }
  .fs-table-wrap::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.18); }

  .fs-table { width: 100%; border-collapse: collapse; font-size: 12px; }

  .fs-table thead th {
    background: rgba(255,255,255,0.04);
    color: #64748b;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 12px 10px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    white-space: nowrap;
    text-align: center;
    user-select: none;
  }

  .fs-table tbody tr.fs-tr-main { cursor: pointer; }
  .fs-table tbody tr.fs-tr-main:hover td { background: rgba(255,255,255,0.03) !important; }
  .fs-table tbody tr.fs-tr-main.selected td { background: rgba(200,255,0,0.045); }

  .fs-table tbody td {
    padding: 10px 10px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    color: #c4cdd8;
    text-align: center;
    white-space: nowrap;
    transition: background 0.15s ease;
  }

  .fs-table tbody td.td-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    color: #d7ff57;
    font-size: 12px;
    letter-spacing: 0.04em;
  }

  .fs-table tbody td.td-num { font-weight: 700; color: #f1f5f9; }
  .fs-table tbody td.td-left { text-align: left; }
  .fs-table tbody td.td-muted { color: #64748b; }

  .fs-checkbox-wrap { display: flex; align-items: center; justify-content: center; }

  .fs-checkbox {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 5px;
    border: 1.5px solid rgba(255,255,255,0.22);
    background: rgba(255,255,255,0.03);
    cursor: pointer;
    transition: all 0.18s ease;
    position: relative;
  }
  .fs-checkbox:hover { border-color: rgba(200,255,0,0.55); box-shadow: 0 0 0 3px rgba(200,255,0,0.08); }
  .fs-checkbox:checked { background: #dfff38; border-color: #dfff38; box-shadow: 0 0 0 3px rgba(223,255,56,0.1); }
  .fs-checkbox:checked::after {
    content: "";
    position: absolute;
    left: 4px; top: 1px;
    width: 4px; height: 8px;
    border: solid #081018;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .fs-expand-btn {
    width: 24px; height: 24px;
    border-radius: 50%;
    border: 1.5px solid rgba(255,255,255,0.14);
    background: transparent;
    color: #64748b;
    font-weight: 900;
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.18s ease;
    line-height: 1;
  }
  .fs-expand-btn.open {
    border-color: #c8ff00;
    background: #c8ff00;
    color: #080c10;
    box-shadow: 0 0 0 4px rgba(200,255,0,0.1);
  }
  .fs-expand-btn:hover { transform: scale(1.12); box-shadow: 0 0 0 3px rgba(200,255,0,0.12); }

  .fs-badge-conforme {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  .fs-badge-conforme.ok  { background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.3); color: #4ade80; }
  .fs-badge-conforme.nok { background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.3); color: #f87171; }
  .fs-badge-conforme.def { background: transparent; border: 1px solid transparent; color: #64748b; }

  .fs-expanded-cell {
    padding: 0 0 0 52px !important;
    background: rgba(200,255,0,0.02) !important;
    border-left: 3px solid rgba(200,255,0,0.14) !important;
    border-bottom: 2px solid rgba(200,255,0,0.1) !important;
  }

  .fs-plis-inner { padding: 16px 16px 16px 0; }

  .fs-plis-title {
    font-family: 'Syne', sans-serif;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #d7ff57;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .fs-plis-table-wrap {
    border-radius: 12px;
    border: 1px solid rgba(200,255,0,0.12);
    overflow: hidden;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.02);
    overflow-x: auto;
  }

  .fs-plis-table { width: 100%; border-collapse: collapse; font-size: 12px; }

  .fs-plis-table thead th {
    background: rgba(200,255,0,0.05);
    color: rgba(215,255,87,0.72);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 9px 14px;
    border-bottom: 1px solid rgba(200,255,0,0.1);
    text-align: center;
    white-space: nowrap;
  }

  .fs-plis-table tbody td {
    padding: 8px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.03);
    color: #94a3b8;
    text-align: center;
    white-space: nowrap;
    font-size: 12px;
  }

  .fs-plis-table tbody td.td-pos {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    color: #d7ff57;
  }

  .fs-plis-table tbody td.td-gain { color: #4ade80; }
  .fs-plis-table tbody td.td-perte { color: #f87171; }

  .fs-plis-table tbody tr:hover td { background: rgba(255,255,255,0.02); }

  .fs-empty {
    padding: 60px 20px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
    letter-spacing: 0.05em;
  }

  .fs-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px 20px 22px;
    border-top: 1px solid rgba(255,255,255,0.04);
  }

  .fs-pagination-shell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px;
    border-radius: 18px;
    background: rgba(255,255,255,0.035);
    border: 1px solid rgba(255,255,255,0.045);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.02), 0 10px 30px rgba(0,0,0,0.14);
    backdrop-filter: blur(8px);
  }

  .fs-page-btn {
    min-width: 38px; height: 38px;
    padding: 0 14px;
    border: none; outline: none;
    border-radius: 12px;
    background: transparent;
    color: #8ea0b3;
    font-size: 13px; font-weight: 700;
    font-family: 'DM Mono', monospace;
    display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
  }
  .fs-page-btn:hover:not(:disabled) { background: rgba(255,255,255,0.055); color: #fff; transform: translateY(-1px); }
  .fs-page-btn.active { background: #e3ff2b; color: #081018; box-shadow: 0 6px 18px rgba(227,255,43,0.22), inset 0 1px 0 rgba(255,255,255,0.35); }
  .fs-page-btn.nav { padding: 0 16px; min-width: auto; color: #a6b4c3; font-size: 13px; font-weight: 700; }
  .fs-page-btn.nav:hover:not(:disabled) { color: #fff; }

  .fs-page-dots {
    min-width: 24px; height: 38px;
    display: inline-flex; align-items: center; justify-content: center;
    color: #728396; font-size: 13px; font-weight: 700;
    user-select: none;
  }

  @keyframes fs-shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }

  .fs-skeleton {
    display: inline-block;
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(90deg,
      rgba(255,255,255,0.04) 25%,
      rgba(255,255,255,0.08) 50%,
      rgba(255,255,255,0.04) 75%
    );
    background-size: 600px 100%;
    animation: fs-shimmer 1.5s infinite;
  }

  @media (max-width: 900px) {
    .fs-toolbar { align-items: stretch; }
    .fs-toolbar-group { width: 100%; }
    .fs-input-search { width: 100%; min-width: 180px; flex: 1; }
    .fs-sep { display: none; }
  }

  @media (max-width: 768px) {
    .fs-pagination { padding: 16px 12px 20px; }
    .fs-pagination-shell { width: 100%; justify-content: center; overflow-x: auto; scrollbar-width: none; }
    .fs-pagination-shell::-webkit-scrollbar { display: none; }
    .fs-page-btn { min-width: 36px; height: 36px; border-radius: 11px; font-size: 12px; }
    .fs-page-btn.nav { padding: 0 12px; font-size: 12px; }
    .fs-table tbody td, .fs-table thead th { padding-left: 8px; padding-right: 8px; }
  }
`;

function injectStyles() {
  if (document.getElementById("fiche-stack-css")) return;
  const el = document.createElement("style");
  el.id = "fiche-stack-css";
  el.innerHTML = CSS;
  document.head.appendChild(el);
}

/* ============================================================
   HELPERS
   ============================================================ */
const ESSENCE_LABELS = {
  PE: "Peuplier", CH: "Chêne", HE: "Hêtre",
  EP: "Épicéa",  PI: "Pin",   BO: "Bouleau", HO: "Houx",
};

function extractEssence(pn1, fallback) {
  if (!pn1 || pn1 === "N/A") return fallback || "—";
  const parts = String(pn1).split(/[-_]/);
  const code  = parts[0]?.toUpperCase();
  const variety = parts[1] || "";
  const ess = ESSENCE_LABELS[code] || fallback || code || "—";
  return variety ? `${ess} (${variety})` : ess;
}

function fmt(val, dec = 2) {
  if (val == null || val === "" || val === "N/A") return "—";
  const n = Number(val);
  return Number.isFinite(n)
    ? n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: dec })
    : String(val);
}

function fmtPct(val, dec = 2) {
  if (val == null || val === "" || val === "N/A") return "—";
  const n = Number(val);
  return Number.isFinite(n) ? `${n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: dec })} %` : "—";
}

/** Gain massique cible : 70–90 % (aligné traitement_thermique_calc.py). */
const GAIN_MASSIQUE_MIN = 70;
const GAIN_MASSIQUE_MAX = 90;

function gainMassiqueClass(gain) {
  if (gain == null || gain === "" || gain === "N/A") return "";
  const n = Number(gain);
  if (!Number.isFinite(n)) return "";
  if (n < GAIN_MASSIQUE_MIN || n > GAIN_MASSIQUE_MAX) return "td-perte";
  return "td-gain";
}

function conformeClass(val) {
  if (!val || val === "N/A") return "def";
  const v = String(val).toUpperCase();
  if (v.includes("NON")) return "nok";
  if (v.includes("CONFORME")) return "ok";
  return "def";
}

function formatTimestamp(value) {
  if (!value || value === "N/A") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("fr-FR");
}

function getILSLabel(ils72h, ilsBdb) {
  const v72  = ils72h && String(ils72h) !== "N/A" ? ils72h : null;
  const vBdb = ilsBdb && String(ilsBdb) !== "N/A" ? ilsBdb : null;
  if (v72)  return `${fmt(v72, 2)} (72h)`;
  if (vBdb) return `${fmt(vBdb, 2)} (BDB)`;
  return "—";
}

function normalizeStack(s) {
  const timestamp = s.Timestamp ?? s.Date ?? s.time ?? null;
  return {
    ID_Stack:               s.StackId ?? s.ID_Stack ?? s.IDStack ?? "—",
    StackId:                s.StackId ?? s.ID_Stack ?? s.IDStack ?? "—",
    PN1:                    s.PN1 ?? null,
    Essence:                s.Essence ?? null,
    MelangePN:              s.Resine ?? s.MelangePN ?? null,
    TotalPlis:              s.NbPlis ?? s.TotalPlis ?? null,
    DureeTraitement:        s.DureeTraitement ?? null,
    Temperature:            s.Temperature ?? null,
    Presse:                 s.Presse ?? null,
    DureeCycle:             s.DureeCycle ?? null,
    CalesUtilisees:         s.Cales ?? s.CalesUtilisees ?? null,
    TraitementApresCuisson: s.TraitementApresCuisson ?? null,
    Longueur:               s.Longueur ?? null,
    Largeur:                s.Largeur ?? null,
    Epaisseur_Moyenne:      s.Epaisseur_Moyenne ?? s.EpaisseurMoyenne ?? null,
    Masse:                  s.Masse ?? null,
    Densite_Stack:          s.Densite_Stack ?? s.DensiteStack ?? s.Densite ?? null,
    Ecart_Max_Ep:           s.Ecart_Max_Ep ?? s.EcartMaxEp ?? null,
    Taux_Compression:       s.Taux_Compression ?? s.TauxCompression ?? null,
    Conformite:             s.Conformite ?? null,
    Bending_Modulus_MPa:    s.Bending_Modulus_MPa ?? s.MOE ?? null,
    Stress_Peak_MPa:        s.Stress_Peak_MPa ?? s.MOR ?? null,
    ILS_72h_MPa:            s.ILS_72h_MPa ?? null,
    ILS_BDB_MPa:            s.ILS_BDB_MPa ?? null,
    plis:                   Array.isArray(s.plis) ? s.plis : [],
    Timestamp:              timestamp,
    Date:                   timestamp,
  };
}

/* ============================================================
   COLONNES
   ============================================================ */
const MAIN_COLS = [
  { key: "select",               label: "",                  width: 54 },
  { key: "expand",               label: "",                  width: 44 },
  { key: "ID_Stack",             label: "ID Stack",          sortable: true },
  { key: "Timestamp",            label: "Date",              sortable: true },
  { key: "Essence",              label: "Essence",           sortable: true },
  { key: "MelangePN",            label: "Résine",            sortable: true },
  { key: "TotalPlis",            label: "Plis",              sortable: true },
  { key: "cond",                 label: "Conditionnement" },
  { key: "Presse",               label: "Presse",            sortable: true },
  { key: "DureeCycle",           label: "Cycle presse",      sortable: true },
  { key: "CalesUtilisees",       label: "Cales",             sortable: true },
  { key: "Longueur",             label: "Long. (mm)",        sortable: true },
  { key: "Largeur",              label: "Larg. (mm)",        sortable: true },
  { key: "Epaisseur_Moyenne",    label: "Ép. Moy. (mm)",     sortable: true },
  { key: "Masse",                label: "Masse (g)",         sortable: true },
  { key: "Densite_Stack",        label: "Densité",           sortable: true },
  { key: "Ecart_Max_Ep",         label: "Écart Ép. (mm)",    sortable: true },
  { key: "Taux_Compression",     label: "Taux Compr. (%)",   sortable: true },
  { key: "Bending_Modulus_MPa",  label: "MOE (MPa)",         sortable: true },
  { key: "Stress_Peak_MPa",      label: "MOR (MPa)",         sortable: true },
  { key: "ils",                  label: "ILS (MPa)" },
  { key: "Conformite",           label: "Conformité",        sortable: true },
];

const PLIS_COLS = [
  "N° Pli",
  "PN",
  "Masse (g)",
  "Humidité (%)",
  "Ép. (mm)",
  "Densité anhydre",
  "Masse cond. (g)",
  "Masse imprég. (g)",
  "Masse séch. (g)",
  "Gain mass. (%)",
  "Humid. séch. (%)",
  "Perte mass. (%)",
  "Commentaire",
];

/* ============================================================
   COMPOSANT
   ============================================================ */
export default function FicheStack() {
  const { t, ui } = useI18n();

  injectStyles();

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingHint, setLoadingHint] = useState("");
  const [err,         setErr]         = useState("");
  const [exporting,   setExporting]   = useState(false);

  const [expanded,    setExpanded]    = useState(new Set());
  const [plisLoading, setPlisLoading] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");

  const [sortKey, setSortKey] = useState("Timestamp");
  const [sortDir, setSortDir] = useState("desc");

  const [pagination, setPagination] = useState({
    page: 1, per_page: 20, total: 0,
    total_pages: 1, has_next: false, has_prev: false,
  });

  const headerCheckboxRef = useRef(null);

  /* ── fetch ── */
  const buildQuery = useCallback((page, perPage, searchValue) => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("per_page", String(perPage));
    if (searchValue?.trim()) p.set("search", searchValue.trim());
    return `/api/rd/stacks?${p.toString()}`;
  }, []);

  const waitForRdCache = useCallback(async (setHint, maxMs = 120000) => {
    const t0 = Date.now();
    while (Date.now() - t0 < maxMs) {
      try {
        const r = await apiFetch("/api/rd/cache-status");
        if (r.ok) {
          const d = await r.json().catch(() => ({}));
          const nb = Number(d.nb_stacks ?? 0);
          if (d.valid || d.built_at) {
            return nb;
          }
          const elapsed = Math.round((Date.now() - t0) / 1000);
          setHint?.(`Construction du cache RD… (${elapsed}s)`);
        }
      } catch {
        /* cache-status indisponible, on retente */
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    throw new Error("Le cache RD met trop de temps à se construire. Réessayez avec ↻ Recharger.");
  }, []);

  const loadStacks = useCallback(
    async function loadStacksInner(
      page = 1,
      perPage = pagination.per_page,
      searchValue = search,
      attempt = 1
    ) {
      const firstLoad = rows.length === 0 && attempt === 1;
      if (firstLoad) {
        setLoading(true);
        setLoadingHint(
          attempt === 1
            ? "Connexion au serveur et préparation du cache…"
            : `Nouvelle tentative (${attempt}/5)…`
        );
      } else if (attempt === 1) {
        setLoadingPage(true);
      }

      setErr("");
      try {
        if (attempt === 1) {
          await waitForRdCache(setLoadingHint);
        }

        const r = await apiFetch(buildQuery(page, perPage, searchValue));
        const data = await r.json().catch(() => null);

        if (!r.ok) {
          throw new Error(data?.message || `Erreur serveur (${r.status})`);
        }
        if (!data || !Array.isArray(data.stacks)) {
          throw new Error("Réponse API invalide — relancez ↻ Recharger.");
        }

        const normalized = data.stacks.map(normalizeStack);
        const pag = data.pagination ?? {
          page,
          per_page: perPage,
          total: normalized.length,
          total_pages: 1,
          has_next: false,
          has_prev: false,
        };

        setRows(normalized);
        setPagination(pag);
        setExpanded(new Set());
        setLoadingHint("");
      } catch (e) {
        if (attempt < 5) {
          setLoadingHint(`Nouvelle tentative (${attempt + 1}/5)…`);
          await new Promise((resolve) => setTimeout(resolve, 2500 * attempt));
          return loadStacksInner(page, perPage, searchValue, attempt + 1);
        }
        setErr(e.message || "Erreur inconnue — réessayez avec ↻ Recharger");
        setLoadingHint("");
      } finally {
        setLoading(false);
        setLoadingPage(false);
      }
    },
    [buildQuery, pagination.per_page, rows.length, search, waitForRdCache]
  );

  useEffect(() => {
    loadStacks(1, pagination.per_page, search);
  }, []);

  /* ── tri client ── */
  const view = useMemo(() => {
    const out = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      if (sortKey === "Timestamp" || sortKey === "Date") {
        const da = new Date(va).getTime();
        const db = new Date(vb).getTime();
        if (!isNaN(da) && !isNaN(db)) return (da - db) * dir;
      }
      const na = Number(va), nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb) && String(va) !== "" && String(vb) !== "") return (na - nb) * dir;
      return String(va).localeCompare(String(vb), "fr") * dir;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  const visibleIds           = useMemo(() => view.map((r) => r.ID_Stack), [view]);
  const allVisibleSelected   = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected  = visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);

  /* ── actions ── */
  const loadPlisForStack = useCallback(async (stackId) => {
    setPlisLoading((prev) => new Set(prev).add(stackId));
    try {
      const r = await apiFetch(`/api/rd/stacks/${encodeURIComponent(stackId)}/plis`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message ?? `Erreur plis (HTTP ${r.status})`);
      setRows((prev) =>
        prev.map((row) =>
          row.ID_Stack === stackId ? { ...row, plis: data.plis ?? [] } : row
        )
      );
    } catch (e) {
      setErr(e.message ?? "Impossible de charger les plis.");
    } finally {
      setPlisLoading((prev) => {
        const next = new Set(prev);
        next.delete(stackId);
        return next;
      });
    }
  }, []);

  const toggleExpand = useCallback(async (id) => {
    if (expanded.has(id)) {
      setExpanded((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      return;
    }
    setExpanded((prev) => new Set(prev).add(id));
    const rowData = rows.find((r) => r.ID_Stack === id);
    if (rowData && !(rowData.plis?.length)) {
      await loadPlisForStack(id);
    }
  }, [expanded, rows, loadPlisForStack]);

  function toggleRowSelection(id) {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  }

  async function handleSearchSubmit() {
    setSearch(searchDraft);
    await loadStacks(1, pagination.per_page, searchDraft);
  }

  async function handleReset() {
    setSearch(""); setSearchDraft(""); setSortKey("Timestamp"); setSortDir("desc");
    setSelectedIds(new Set());
    await loadStacks(1, pagination.per_page, "");
  }

  async function handleChangePage(nextPage) {
    if (nextPage < 1 || nextPage > pagination.total_pages) return;
    await loadStacks(nextPage, pagination.per_page, search);
  }

  async function handleChangePerPage(nextPerPage) {
    await loadStacks(1, Number(nextPerPage), search);
  }

  async function handleRefresh() {
    await loadStacks(pagination.page, pagination.per_page, search);
  }

  async function handleExportExcel() {
    if (!selectedIds.size || exporting) return;
    setExporting(true);
    try {
      const r = await apiFetch("/api/rd/export-excel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stack_ids: Array.from(selectedIds) }),
      });
      if (r.status === 403) throw new Error("Accès refusé à cette section");
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || `Erreur export ${r.status}`);
      }
      const blob      = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a         = document.createElement("a");
      a.href          = objectUrl;
      a.download      = `stacks-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      alert(`Export échoué : ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  /* ── pagination numbers ── */
  const pageNumbers = useMemo(() => {
    const total = pagination.total_pages || 1;
    const cur   = pagination.page || 1;
    const pages = [];
    const start = Math.max(1, cur - 1);
    const end   = Math.min(total, cur + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [pagination]);

  /* ── skeleton ── */
  function SkeletonRows() {
    return Array.from({ length: 6 }).map((_, i) => (
      <tr key={i} className="fs-tr-main">
        {MAIN_COLS.map((c, j) => (
          <td key={j}>
            {c.key === "select" ? (
              <span className="fs-skeleton" style={{ width: 16, height: 16, borderRadius: 5 }} />
            ) : c.key === "expand" ? (
              <span className="fs-skeleton" style={{ width: 24, height: 24, borderRadius: "50%" }} />
            ) : (
              <span className="fs-skeleton" style={{ width: j === 2 ? 90 : 52, opacity: 1 - i * 0.1 }} />
            )}
          </td>
        ))}
      </tr>
    ));
  }

  /* ── render plis ── */
  function renderPlis(r) {
    const nbPlis = r.plis?.length ?? 0;
    const loadingPlis = plisLoading.has(r.ID_Stack);
    return (
      <tr key={`${r.ID_Stack}-plis`}>
        <td colSpan={MAIN_COLS.length} className="fs-expanded-cell">
          <div className="fs-plis-inner">
            <div className="fs-plis-title">
              <span>▤</span>
              <span>Plis de {r.ID_Stack}</span>
              {nbPlis > 0 && (
                <span style={{ color: "rgba(200,255,0,0.55)", fontWeight: 500 }}>
                  — {nbPlis} pli{nbPlis > 1 ? "s" : ""}
                </span>
              )}
            </div>

            {loadingPlis ? (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: "8px 0" }}>
                Chargement des plis…
              </div>
            ) : nbPlis === 0 ? (
              <div style={{ fontSize: 12, color: "#64748b", padding: "8px 0" }}>
                Aucun pli enregistré pour ce stack.
              </div>
            ) : (
              <div className="fs-plis-table-wrap">
                <table className="fs-plis-table">
                  <thead>
                    <tr>
                      {PLIS_COLS.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {r.plis.map((p, pi) => {
                      const gain  = p["Gain massique (%)"]  ?? p["Gain_massique"]  ?? p["Gain massique"];
                      const humidSech = p["Humidité après séchage (%)"] ?? p["HumiditeApresSechage"] ?? null;
                      const perte = p["Perte massique (%)"] ?? p["Perte_massique"] ?? p["Perte massique"];
                      const masseCond  = p["Masse cond. (g)"]   ?? p["Masse_cond"]  ?? null;
                      const masseImpreg = p["Masse imprég. (g)"] ?? p["Masse_impreg"] ?? null;
                      const masseSech  = p["Masse séch. (g)"]   ?? p["Masse_sech"]  ?? null;
                      const humidSechNum = humidSech != null && humidSech !== "N/A" ? parseFloat(humidSech) : null;
                      const humidSechOut = humidSechNum != null && Number.isFinite(humidSechNum)
                        ? (humidSechNum < 3 || humidSechNum > 10 ? "warn" : "ok")
                        : null;
                      return (
                        <tr key={pi}>
                          <td className="td-pos">{p.num ?? p.Position ?? p.position ?? pi + 1}</td>
                          <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>
                            {p.PlyPN || "—"}
                          </td>
                          <td>{fmt(p.Masse ?? p["Masse (g)"], 1)}</td>
                          <td>{fmt(p.Humidite_Moyenne ?? p["Humidité Merlin (%)"] ?? p["Humidité (%)"], 1)}</td>
                          <td>{fmt(p.Epaisseur ?? p["Epaisseur (mm)"], 2)}</td>
                          <td>{fmt(p.Densite_anhydre ?? p["Densité anhydre"], 2)}</td>
                          <td>{fmt(masseCond, 1)}</td>
                          <td>{fmt(masseImpreg, 1)}</td>
                          <td>{fmt(masseSech, 1)}</td>
                          <td className={gainMassiqueClass(gain)}>{fmtPct(gain, 4)}</td>
                          <td className={humidSechOut === "warn" ? "td-perte" : humidSechOut === "ok" ? "td-gain" : ""}>
                            {humidSechNum != null && Number.isFinite(humidSechNum) ? `${humidSechNum.toFixed(4)} %` : "—"}
                          </td>
                          <td className="td-perte">{fmtPct(perte)}</td>
                          <td style={{ textAlign: "left", color: "#64748b" }}>
                            {p.Commentaire && p.Commentaire !== "N/A" && p.Commentaire !== " "
                              ? p.Commentaire : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  }

  /* ── JSX ── */
  return (
    <div className="fs-root">
      <MenuBar active="rd" />

      <div className="fs-wrap">
        {/* Topbar */}
        <div className="fs-topbar">
          <div className="fs-title-group">
            <span className="fs-badge">R&amp;D</span>
            <span className="fs-title">Fiches Stacks</span>
            {!loading && (
              <span className="fs-count-chip">
                {pagination.total} stack{pagination.total > 1 ? "s" : ""}
              </span>
            )}
            {!!selectedIds.size && (
              <span className="fs-count-chip">
                {selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        <div className="fs-card">
          <div className="fs-card-header">
            <span>▣</span>
            <span>Tableau des Stacks</span>
          </div>

          {/* Toolbar */}
          <div className="fs-toolbar">
            {/* Recherche */}
            <div className="fs-toolbar-group">
              <span className="fs-label">Recherche</span>
              <input
                className="fs-input fs-input-search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearchSubmit(); }}
                placeholder="Rechercher un stack, essence, résine…"
              />
              <button className="fs-btn fs-btn-accent" onClick={handleSearchSubmit}>
                Rechercher
              </button>
            </div>

            <div className="fs-sep" />

            {/* Tri */}
            <div className="fs-toolbar-group">
              <span className="fs-label">{ui("Trier par")}</span>
              <select className="fs-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                {MAIN_COLS.filter((c) => c.sortable).map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
              <button className="fs-btn fs-btn-accent" onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}>
                {sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
              </button>
            </div>

            <div className="fs-sep" />

            {/* Lignes / page */}
            <div className="fs-toolbar-group">
              <span className="fs-label">Lignes / page</span>
              <select className="fs-select" value={pagination.per_page} onChange={(e) => handleChangePerPage(e.target.value)}>
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div className="fs-sep" />

            {/* Sélection */}
            <div className="fs-toolbar-group">
              <span className="fs-label">Sélection</span>
              <button className="fs-btn fs-btn-ghost" onClick={toggleSelectAllVisible} disabled={!view.length}>
                {allVisibleSelected ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
              <button className="fs-btn fs-btn-ghost" onClick={() => setSelectedIds(new Set())} disabled={!selectedIds.size}>
                Vider
              </button>
            </div>

            <div className="fs-sep" />

            <button className="fs-btn fs-btn-ghost" onClick={handleReset}>✕ Reset</button>
            <button className="fs-btn fs-btn-ghost" onClick={handleRefresh} disabled={loadingPage}>↻ Recharger</button>

            <div className="fs-sep" />

            <button className="fs-btn fs-btn-ghost" onClick={() => setExpanded(new Set(view.map((r) => r.ID_Stack)))} disabled={!view.length}>
              ＋ Tout ouvrir
            </button>
            <button className="fs-btn fs-btn-ghost" onClick={() => setExpanded(new Set())} disabled={!expanded.size}>
              − Tout fermer
            </button>

            <div className="fs-sep" />

            {/* Export Excel */}
            <button className="fs-btn fs-btn-excel" onClick={handleExportExcel} disabled={exporting || !selectedIds.size}>
              {exporting ? "⏳ Export…" : `↓ Export Excel (${selectedIds.size})`}
            </button>
          </div>

          {loadingPage && !loading && (
            <div className="fs-loading-banner">Chargement de la page…</div>
          )}

          {loading && loadingHint && (
            <div className="fs-loading-banner">{loadingHint}</div>
          )}

          {/* Table */}
          <div className="fs-table-wrap">
            <table className="fs-table">
              <thead>
                <tr>
                  {MAIN_COLS.map((c, i) => {
                    if (c.key === "select") {
                      return (
                        <th key={i} style={{ width: c.width }}>
                          <div className="fs-checkbox-wrap">
                            <input
                              ref={headerCheckboxRef}
                              type="checkbox"
                              className="fs-checkbox"
                              checked={allVisibleSelected}
                              onChange={toggleSelectAllVisible}
                            />
                          </div>
                        </th>
                      );
                    }
                    return (
                      <th
                        key={i}
                        style={{ width: c.width ?? "auto", cursor: c.sortable ? "pointer" : "default" }}
                        onClick={c.sortable ? () => {
                          if (sortKey === c.key) setSortDir((d) => d === "asc" ? "desc" : "asc");
                          else { setSortKey(c.key); setSortDir("asc"); }
                        } : undefined}
                      >
                        {c.label}
                        {c.sortable && sortKey === c.key && (
                          <span style={{ marginLeft: 4, color: "#c8ff00" }}>
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {loading && <SkeletonRows />}

                {!loading && err && (
                  <tr>
                    <td colSpan={MAIN_COLS.length} className="fs-empty" style={{ color: "#f87171" }}>
                      ⚠ {err}
                    </td>
                  </tr>
                )}

                {!loading && !err && view.length === 0 && (
                  <tr>
                    <td colSpan={MAIN_COLS.length} className="fs-empty">
                      Aucun stack trouvé
                    </td>
                  </tr>
                )}

                {!loading && !err && view.map((r, i) => {
                  const isOpen     = expanded.has(r.ID_Stack);
                  const isSelected = selectedIds.has(r.ID_Stack);
                  const essence    = extractEssence(r.PN1, r.Essence);
                  const cond       = r.DureeTraitement && r.Temperature
                    ? `${r.DureeTraitement}h à ${r.Temperature}°C`
                    : "—";
                  const ils = getILSLabel(r.ILS_72h_MPa, r.ILS_BDB_MPa);

                  return [
                    <tr
                      key={r.ID_Stack}
                      className={`fs-tr-main${isSelected ? " selected" : ""}`}
                      style={{ background: i % 2 === 1 ? "rgba(255,255,255,0.015)" : "transparent" }}
                      onClick={() => toggleExpand(r.ID_Stack)}
                    >
                      {/* Checkbox */}
                      <td onClick={(e) => e.stopPropagation()} style={{ padding: "10px 6px" }}>
                        <div className="fs-checkbox-wrap">
                          <input
                            type="checkbox"
                            className="fs-checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelection(r.ID_Stack)}
                          />
                        </div>
                      </td>

                      {/* Expand */}
                      <td style={{ padding: "10px 6px" }}>
                        <button
                          className={`fs-expand-btn${isOpen ? " open" : ""}`}
                          onClick={(e) => { e.stopPropagation(); toggleExpand(r.ID_Stack); }}
                          title={isOpen ? "Masquer les plis" : "Voir les plis"}
                        >
                          {isOpen ? "−" : "+"}
                        </button>
                      </td>

                      <td className="td-id">{r.ID_Stack}</td>
                      <td>{formatTimestamp(r.Timestamp)}</td>
                      <td>{essence}</td>
                      <td>{r.MelangePN ?? "—"}</td>
                      <td className="td-num">{r.TotalPlis ?? "—"}</td>
                      <td>{cond}</td>
                      <td>{r.Presse ?? "—"}</td>
                      <td>{r.DureeCycle ?? "—"}</td>
                      <td>{r.CalesUtilisees ?? "—"}</td>
                      <td>{fmt(r.Longueur, 0)}</td>
                      <td>{fmt(r.Largeur, 0)}</td>
                      <td>{fmt(r.Epaisseur_Moyenne, 2)}</td>
                      <td>{fmt(r.Masse, 0)}</td>
                      <td>{fmt(r.Densite_Stack, 2)}</td>
                      <td>{fmt(r.Ecart_Max_Ep, 2)}</td>
                      <td>{fmt(r.Taux_Compression, 1)}</td>
                      <td className="td-num">{fmt(r.Bending_Modulus_MPa, 0)}</td>
                      <td className="td-num">{fmt(r.Stress_Peak_MPa, 1)}</td>
                      <td>{ils}</td>
                      <td>
                        <span className={`fs-badge-conforme ${conformeClass(r.Conformite)}`}>
                          {r.Conformite || "—"}
                        </span>
                      </td>
                    </tr>,

                    isOpen && renderPlis(r),
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && pagination.total_pages > 1 && (
            <div className="fs-pagination">
              <div className="fs-pagination-shell">
                <button
                  className="fs-page-btn nav"
                  onClick={() => handleChangePage(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                >
                  ← Préc
                </button>

                {pageNumbers[0] > 1 && (
                  <>
                    <button className="fs-page-btn" onClick={() => handleChangePage(1)}>1</button>
                    {pageNumbers[0] > 2 && <span className="fs-page-dots">…</span>}
                  </>
                )}

                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    className={`fs-page-btn${n === pagination.page ? " active" : ""}`}
                    onClick={() => handleChangePage(n)}
                  >
                    {n}
                  </button>
                ))}

                {pageNumbers[pageNumbers.length - 1] < pagination.total_pages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < pagination.total_pages - 1 && (
                      <span className="fs-page-dots">…</span>
                    )}
                    <button className="fs-page-btn" onClick={() => handleChangePage(pagination.total_pages)}>
                      {pagination.total_pages}
                    </button>
                  </>
                )}

                <button
                  className="fs-page-btn nav"
                  onClick={() => handleChangePage(pagination.page + 1)}
                  disabled={!pagination.has_next}
                >
                  Suiv →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
